'use client'

import * as React from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'
import { nl } from 'date-fns/locale'
import { cn } from '../lib/utils'
import { Button } from './button'
import { Calendar } from './calendar'
import { Input } from './input'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface DatePickerProps {
  value: string | undefined
  onChange: (value: string | undefined) => void
  placeholder?: string
  disabled?: boolean
  fromDate?: Date
  toDate?: Date
  fromYear?: number
  toYear?: number
  captionLayout?: 'buttons' | 'dropdown' | 'dropdown-buttons'
  allowManualInput?: boolean
  showTodayButton?: boolean
  /**
   * Toon ISO-weeknummers links van de dagen. Default `false`. Wordt
   * doorgegeven aan de onderliggende `Calendar`.
   */
  showWeekNumbers?: boolean
  required?: boolean
  className?: string
  id?: string
  'aria-label'?: string
}

function isoToDate(iso: string | undefined): Date | undefined {
  if (!iso) return undefined
  const d = parseISO(iso)
  return isValid(d) ? d : undefined
}

function dateToIso(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

function tryParseManualInput(raw: string): Date | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const dmy = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/)
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))
    if (isValid(d) && d.getDate() === Number(dmy[1]) && d.getMonth() === Number(dmy[2]) - 1) {
      return d
    }
  }
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    if (isValid(d) && d.getDate() === Number(iso[3]) && d.getMonth() === Number(iso[2]) - 1) {
      return d
    }
  }
  return null
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Kies een datum',
  disabled,
  fromDate,
  toDate,
  fromYear,
  toYear,
  captionLayout,
  allowManualInput = false,
  showTodayButton = true,
  showWeekNumbers = false,
  required,
  className,
  id,
  'aria-label': ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const selected = isoToDate(value)

  const formatForInput = React.useCallback(
    (d: Date | undefined) => (d ? format(d, 'dd-MM-yyyy') : ''),
    [],
  )

  const [textInput, setTextInput] = React.useState(() => formatForInput(selected))

  // Sync alleen op de `value`-string. `selected` is elke render een nieuw
  // Date-object; stond die in de deps, dan draaide dit effect na élke
  // re-render en werd elke toetsaanslag direct teruggezet naar de oude
  // datum — typen in een gevuld veld was daardoor onmogelijk.
  React.useEffect(() => {
    setTextInput(formatForInput(isoToDate(value)))
  }, [value, formatForInput])

  // Wie een jaarbereik (`fromYear`/`toYear`) meegeeft, wil naar een ver
  // verleden/toekomst kunnen springen — bv. geboortedatum (1940..) of een
  // verloopdatum. Zonder expliciete `captionLayout` zou react-day-picker dan
  // terugvallen op maand-voor-maand-knoppen (tergend traag om jaren terug te
  // gaan). Default daarom naar jaar+maand-dropdowns zodra er een bereik is.
  const effectiveCaptionLayout =
    captionLayout ??
    (fromYear != null || toYear != null ? 'dropdown-buttons' : undefined)

  const dropdownDefaults =
    effectiveCaptionLayout === 'dropdown' || effectiveCaptionLayout === 'dropdown-buttons'
      ? {
          fromYear: fromYear ?? 1940,
          toYear: toYear ?? new Date().getFullYear() + 1,
        }
      : { fromYear, toYear }

  const commitManualInput = () => {
    if (!textInput.trim()) {
      onChange(undefined)
      return
    }
    const parsed = tryParseManualInput(textInput)
    if (parsed) {
      onChange(dateToIso(parsed))
    } else {
      setTextInput(formatForInput(selected))
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {allowManualInput ? (
        <div className={cn('flex', className)}>
          <Input
            id={id}
            type="text"
            inputMode="numeric"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onBlur={commitManualInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitManualInput()
              }
            }}
            placeholder="dd-mm-jjjj"
            disabled={disabled}
            aria-label={ariaLabel}
            aria-required={required}
            className="rounded-r-none"
          />
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              aria-label="Kalender openen"
              className="rounded-l-none border-l-0 px-3"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </div>
      ) : (
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            aria-label={ariaLabel}
            aria-required={required}
            className={cn(
              'w-full justify-start text-left font-normal',
              !selected && 'text-[#5C5C58]',
              className,
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selected ? format(selected, 'd MMM yyyy', { locale: nl }) : placeholder}
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent
        // De kalender mag nooit buiten het scherm vallen: in een modal is de
        // pagina erachter vergrendeld, dus wat onder de rand valt is
        // onbereikbaar. Begrens op de ruimte die Radix meet en scroll daarbinnen.
        className="w-auto max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0"
        align="start"
        collisionPadding={8}
        // De scroll-vergrendeling van een omliggende Dialog blokkeert wiel/touch
        // in deze geportalde popover; laat die events hier niet doorbubbelen.
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            onChange(d ? dateToIso(d) : undefined)
            setOpen(false)
          }}
          fromDate={fromDate}
          toDate={toDate}
          captionLayout={effectiveCaptionLayout}
          fromYear={dropdownDefaults.fromYear}
          toYear={dropdownDefaults.toYear}
          showWeekNumbers={showWeekNumbers}
          initialFocus
        />
        {showTodayButton && (
          <div className="border-t border-[#CED0C8] p-2 flex justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange(dateToIso(new Date()))
                setOpen(false)
              }}
            >
              Vandaag
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  onChange(undefined)
                  setOpen(false)
                }}
              >
                Wissen
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
